import { useCallback, useRef, useState } from "react";
import { useAsyncHandler } from "../../../../hooks/useAsyncHandler";
import {
  useConfirmHandler,
  type ConfirmHandlerOptions,
  type UseConfirmHandlerResult,
} from "../../../../hooks/useConfirmHandler";
import { applyBatchJobItem, dismissBatchJobItem, type BatchJob } from "../../../../lib/corpusApi";
import type { BatchJobItemRow } from "./batch_job_item_rows";

/** The confirmation before applying one item, whose message can name what it would do. */
export interface ApplyItemCopy<Result> extends Omit<ConfirmHandlerOptions, "message"> {
  message: (row: BatchJobItemRow<Result> | null) => string;
}

export interface UseBatchJobItemReviewOptions<Result> {
  job: BatchJob;
  /** Refetches the list after an item is settled. */
  refresh: () => Promise<void>;
  applyCopy: ApplyItemCopy<Result>;
}

export interface UseBatchJobItemReviewResult<Result> {
  /** Dismisses one item — the finding is set aside and the existing state stands. */
  keep: {
    handle: (row: BatchJobItemRow<Result>) => void;
    /** Id of the item a keep is in flight (or last failed) for, if any. */
    keepingId: string | null;
    loading: boolean;
    error: string | null;
  };
  /** Applies one item's finding, behind a confirmation. */
  apply: {
    open: (row: BatchJobItemRow<Result>) => void;
    dialogProps: UseConfirmHandlerResult["dialogProps"];
  };
}

/** Settles single items of a batch job: keep one, or apply one behind a confirmation. Both refetch
 *  the list afterwards. Several at once is useBulkItemReview. */
export function useBatchJobItemReview<Result>({
  job,
  refresh,
  applyCopy,
}: UseBatchJobItemReviewOptions<Result>): UseBatchJobItemReviewResult<Result> {
  const [applyTarget, setApplyTarget] = useState<BatchJobItemRow<Result> | null>(null);
  const [keepingId, setKeepingId] = useState<string | null>(null);
  const keepingIdRef = useRef<string | null>(null);

  const applyConfirm = useConfirmHandler({ ...applyCopy, message: applyCopy.message(applyTarget) });

  const openApply = useCallback(
    (row: BatchJobItemRow<Result>) => {
      setApplyTarget(row);
      applyConfirm.handle(async () => {
        await applyBatchJobItem(job.id, row.item.id);
        await refresh();
      });
    },
    [applyConfirm, job.id, refresh]
  );

  const keep = useAsyncHandler(async () => {
    const id = keepingIdRef.current;
    if (!id) return;
    await dismissBatchJobItem(job.id, id);
    await refresh();
  });

  const handleKeep = useCallback(
    (row: BatchJobItemRow<Result>) => {
      keepingIdRef.current = row.item.id;
      setKeepingId(row.item.id);
      void keep.handle();
    },
    [keep]
  );

  return {
    keep: { handle: handleKeep, keepingId, loading: keep.loading, error: keep.error },
    apply: { open: openApply, dialogProps: applyConfirm.dialogProps },
  };
}
